import React from 'react';
import dayjs from 'dayjs';
import { releasingEnabled } from '~/config/public/environment';
import Auction, { AuctionType } from '~/App/shared/interfaces/Auction';
import ProcessObject from '~/App/shared/interfaces/ProcessObject';
import { isEmpty } from '~/App/shared/utils';
import { auctionTypeIsBidding } from '~/helpers/buyCar';
import { isServiceExcludedForVehicleType } from '~/helpers/additionalServices';
import { CMSDataObjectPageService } from '~/App/views/ObjectPage/cmsDataHandlers';
import ReleasingService from './Releasing/ReleasingService';
import ServiceAgreementReleasing from './AgreementReleasingService';
import FinanceService from './FinanceService';
import WarrantyService from './WarrantyService';
import TransportService from './TransportService';
import ServiceAgreement from './AgreementService';
import GeneralService from './GeneralService';

interface ServicesSwitcherProps {
  cmsData: CMSDataObjectPageService;
  auction: Auction;
  auctionCost: number;
  buyMethod: AuctionType | null;
  isReleasingServiceVisible?: boolean;
  isFinanceServiceVisible?: boolean;
  showReleasingForm?: boolean;
  isServiceAgreementReleasingVisible?: boolean;
  releasingInformation?: Record<string, undefined>;
  releasingInformationAccordion?: Record<string, unknown>;
  setReleasingInformationAccordion?: (
    releasing: Record<string, unknown>
  ) => void;
  orderableServices?: {
    finance?: boolean;
    warranty?: boolean;
    transport?: boolean;
  };
  shouldCalculateResidualLoan?: boolean;
  processObject?: ProcessObject;
  isCertified?: boolean;
}

export const ServicesSwitcher = ({
  cmsData,
  auction,
  auctionCost,
  buyMethod,
  isReleasingServiceVisible = true,
  isFinanceServiceVisible = true,
  isServiceAgreementReleasingVisible = false,
  showReleasingForm = true,
  releasingInformation = {},
  releasingInformationAccordion = {},
  setReleasingInformationAccordion,
  orderableServices = {},
  shouldCalculateResidualLoan = false,
  processObject,
  isCertified = false
}: ServicesSwitcherProps) => {
  const { serviceType, active, excludeFromVehicleTypes } = cmsData ?? {};

  if (!serviceType || !active) return null;

  if (
    isServiceExcludedForVehicleType(
      excludeFromVehicleTypes,
      processObject?.vehicleType
    )
  ) {
    return null;
  }

  const isBuyNow = buyMethod === AuctionType.BUY_NOW;
  const isBidding = auctionTypeIsBidding(auction?.auctionType);
  const properties = processObject?.properties;
  const year = dayjs(dayjs().year());
  const manufactureYear = properties?.modelYear
    ? dayjs(properties.modelYear)
    : null;
  const carAge = manufactureYear ? year.diff(manufactureYear, 'year') : 0;
  const odometerReading = properties?.odometerReading || 1000;
  const initialMonthlyReleasingCost = auction.releasing?.monthlyCost || 0;
  const registrationPlate = properties?.registrationPlate || '';

  switch (serviceType) {
    case 'LEASING':
      if (
        !isReleasingServiceVisible ||
        !releasingEnabled ||
        !processObject?.isReleasable
      ) {
        return null;
      }
      return (
        <ReleasingService
          auction={auction}
          isBuyNow={isBuyNow}
          cmsData={cmsData}
          registrationPlate={registrationPlate}
          initialMonthlyReleasingCost={initialMonthlyReleasingCost}
          showReleasingForm={showReleasingForm}
          setReleasingInformation={setReleasingInformationAccordion}
        />
      );

    case 'SERVICE_DEAL_RELEASING':
      if (!isServiceAgreementReleasingVisible) {
        return null;
      }
      return (
        <ServiceAgreementReleasing
          cmsData={cmsData}
          releasingInformation={
            isEmpty(releasingInformation)
              ? releasingInformationAccordion
              : releasingInformation
          }
        />
      );

    case 'FINANCING':
      if (!isFinanceServiceVisible || !orderableServices?.finance) {
        return null;
      }
      return (
        <FinanceService
          isBuyNow={isBuyNow}
          auctionCost={auctionCost}
          cmsData={cmsData}
          shouldCalculateResidualLoan={shouldCalculateResidualLoan}
        />
      );

    case 'SELL_OBJECT':
    case 'GUARANTEE':
    case 'GENERAL':
      if (isServiceAgreementReleasingVisible) {
        return null;
      }
      return <GeneralService cmsData={cmsData} />;

    case 'WARRANTY':
      if (!orderableServices?.warranty) {
        return null;
      }
      return (
        <WarrantyService
          carAge={carAge}
          cmsData={cmsData}
          odometerReading={odometerReading}
        />
      );

    case 'TRANSPORT':
      if (!isBidding || !orderableServices?.transport || !processObject) {
        return null;
      }
      return (
        <TransportService cmsData={cmsData} processObject={processObject} />
      );

    case 'SERVICE_DEAL':
      if (isServiceAgreementReleasingVisible || !isCertified) {
        return null;
      }
      return <ServiceAgreement cmsData={cmsData} />;

    default:
      return null;
  }
};
